import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useApp } from '../store';
import { displayName, initials } from '../people';
import VendorTag from './VendorTag';
import { effectiveRole, can } from '../permissions';
import Icon from './Icon';

/**
 * THE PAYMENT-APPROVER PICKER — who may approve a payment line (project default,
 * or a per-line override). Modelled on CoverPicker so it reads and behaves like
 * every other `.sr-ta-*` typeahead (Escape / Cancel / click-away), with one thing
 * cover does not need: ELIGIBILITY. Only a board manager, owner or workspace admin
 * can approve (they are the ones who can write the ledger at all), so the list is
 * split — people who CAN approve are offered; a searched teammate who can't is
 * shown DISABLED with the reason, never silently missing. A footer states the rule
 * and links to where a role is changed. The current choice carries a tick.
 */
export default function ApproverPicker({
  current,
  onPick,
  onCancel,
  boundary,
  onGotoPeople,
}: {
  /** The email currently set (project default, or the line's override). */
  current?: string;
  onPick: (email: string) => void;
  onCancel: () => void;
  /** What counts as "inside" for click-away (the embedding form), like CoverPicker. */
  boundary?: RefObject<HTMLElement | null>;
  /** Open Workspace → People, where a role is changed. Omitted → the link is hidden. */
  onGotoPeople?: () => void;
}) {
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const projectRoles = useApp((s) => s.projectRoles);
  const legacyRoles = useApp((s) => s.legacyRoles);
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const owner = projects.find((p) => p.id === projectId)?.owner?.toLowerCase() ?? '';

  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!boundary) input.current?.focus();
  }, [boundary]);
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const scope = boundary?.current ?? panel.current;
      if (!scope?.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [onCancel, boundary]);

  const cur = current?.toLowerCase() ?? '';
  const needle = q.trim().toLowerCase();

  // Everyone on the board, each tagged with whether they may approve and why not.
  const { can: canList, cannot } = useMemo(() => {
    const rows = [...new Set(access.emails.map((e) => e.toLowerCase()))]
      .map((e) => {
        const role = effectiveRole({ email: e, admins: access.admins, defaults: roles, on: projectRoles, legacy: legacyRoles, projectId });
        const isOwner = e === owner;
        const eligible = isOwner || can(role, 'editSettings');
        const roleWord = role === 'admin' ? 'workspace admin' : isOwner ? 'board owner' : role;
        return { email: e, name: displayName(e, profiles), position: profiles[e]?.title?.trim() ?? '', eligible, roleWord };
      })
      .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.email.includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { can: rows.filter((p) => p.eligible).slice(0, 8), cannot: rows.filter((p) => !p.eligible).slice(0, 4) };
  }, [access.emails, access.admins, roles, projectRoles, legacyRoles, projectId, owner, profiles, needle]);

  return (
    <div className="cover-picker ap-picker" ref={panel}>
      <input
        ref={input}
        className="input sr-ta-input"
        placeholder="Search a teammate by name or email…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canList.length) {
            e.preventDefault();
            onPick(canList[0].email);
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
      />
      <div className="sr-ta-body cover-picker-body">
        {canList.length === 0 && cannot.length === 0 ? (
          <p className="sr-ta-empty">No teammates match.</p>
        ) : (
          <>
            {canList.length > 0 && <p className="ap-group">Can approve</p>}
            {canList.map((p) => (
              <button className="sr-ta-opt" key={p.email} onClick={() => onPick(p.email)}>
                <span className="sr-ta-av" aria-hidden="true">{initials(p.email, profiles)}</span>
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-nameline"><span className="sr-ta-name">{p.name}</span><VendorTag who={p.email} profiles={profiles} compact /></span>
                  <span className="sr-ta-sub">{p.position ? `${p.position} · ${p.roleWord}` : p.roleWord}</span>
                </span>
                {p.email === cur && <Icon name="check" size={16} />}
              </button>
            ))}
            {cannot.length > 0 && <p className="ap-group">Not eligible</p>}
            {cannot.map((p) => (
              <div className="sr-ta-opt is-ineligible" key={p.email} aria-disabled="true">
                <span className="sr-ta-av" aria-hidden="true">{initials(p.email, profiles)}</span>
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-nameline"><span className="sr-ta-name">{p.name}</span><VendorTag who={p.email} profiles={profiles} compact /></span>
                  <span className="sr-ta-sub ap-whynot">{p.roleWord} — can’t approve payments</span>
                </span>
                <Icon name="lock" size={14} />
              </div>
            ))}
          </>
        )}
      </div>
      <div className="ap-rule">
        Only board managers, owners and workspace admins can approve payments.
        {onGotoPeople && (
          <> Change a role in <button className="ap-link" onClick={onGotoPeople}>Workspace → People</button>.</>
        )}
      </div>
    </div>
  );
}
